import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";

import OpenVocabPromptsPanel from "components/control/settings/OpenVocabPromptsPanel";
import PostProcessRulesPanel from "components/control/settings/PostProcessRulesPanel";
import {
    applyBboxOverlaySource,
    disableAllRecognitionEngines,
    fetchInferSettings,
    fetchInferSource,
    probeRecognitionEngines,
    putInferImgsz,
    putInferSource,
    type InferModelEntry,
    type InferenceSourceId,
    type InferenceSourceEntry,
    type RecognitionEnginesStatus,
} from "lib/api/objectRecognition";
import { getBboxServiceUrl } from "lib/utils/connection";
import { authedFetch } from "lib/utils/authedFetch";
import { useARC } from "providers/ARC/ARCProvider";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { useBboxOverlaySource } from "providers/BboxOverlaySourceProvider";
import { useWebRTC } from "providers/WebRTCProvider";
import { useToast } from "providers/ToastProvider";

const ENGINES_DOWN: RecognitionEnginesStatus = {
    yolo26: { up: false, enabled: false },
};

/** Detection method: text prompts (YOLOE) vs closed-vocab uploaded .pt. */
type YoloInferenceMode = "open_vocab" | "uploaded_model";

const YOLO_INFERENCE_MODE_KEY = "webapp2.control.yoloInferenceMode";

/** Inference resolution bounds (matches backend IMGSZ_MIN/MAX). */
const IMGSZ_DEFAULT = 640;
const IMGSZ_MIN = 160;
const IMGSZ_MAX = 1920;

function loadYoloInferenceMode(): YoloInferenceMode {
    try {
        const r = localStorage.getItem(YOLO_INFERENCE_MODE_KEY);
        if (r === "open_vocab" || r === "uploaded_model") return r;
    } catch {
        /* ignore */
    }
    return "uploaded_model";
}

function storeYoloInferenceMode(mode: YoloInferenceMode): void {
    try {
        localStorage.setItem(YOLO_INFERENCE_MODE_KEY, mode);
    } catch {
        /* ignore */
    }
}

export type RecognitionClassRow = {
    id: number;
    name: string;
    enabled: boolean;
    min_conf: number;
};

type RecognitionResponse = {
    enabled?: boolean;
    classes: RecognitionClassRow[];
};

function requireServiceUrl(url: string | null): string {
    if (!url) throw new Error("Fleet proxy host is unavailable");
    return url;
}

async function fetchRecognition(
    host: string,
    isSecure?: boolean,
    arcId?: string | null
): Promise<{ enabled: boolean; classes: RecognitionClassRow[] }> {
    const url = requireServiceUrl(getBboxServiceUrl(host, "recognition", isSecure, arcId));
    const res = await authedFetch(url);
    if (!res.ok) {
        throw new Error("Request failed. Try again.");
    }
    const data = (await res.json()) as RecognitionResponse;
    if (!data.classes || !Array.isArray(data.classes)) {
        throw new Error("Invalid recognition payload");
    }
    return {
        enabled: data.enabled !== false,
        classes: data.classes,
    };
}

async function putRecognition(
    host: string,
    payload: { enabled: boolean; classes: RecognitionClassRow[] },
    isSecure?: boolean,
    arcId?: string | null
): Promise<void> {
    const url = requireServiceUrl(getBboxServiceUrl(host, "recognition", isSecure, arcId));
    const res = await authedFetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            enabled: payload.enabled,
            classes: payload.classes,
        }),
    });
    if (!res.ok) {
        let detail = res.statusText;
        try {
            const err = (await res.json()) as { error?: string };
            if (err.error) detail = err.error;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
}

type ModelListResponse = {
    models: InferModelEntry[];
    active: string;
    open_vocab?: boolean;
    model_dir?: string;
};

async function fetchModelList(host: string, isSecure?: boolean, arcId?: string | null): Promise<ModelListResponse> {
    const res = await authedFetch(requireServiceUrl(getBboxServiceUrl(host, "model/list", isSecure, arcId)));
    if (!res.ok) {
        throw new Error("Request failed. Try again.");
    }
    const data = (await res.json()) as ModelListResponse;
    if (!data.models || !Array.isArray(data.models)) {
        throw new Error("Invalid model list");
    }
    data.models = data.models.map((m) => ({
        name: String(m.name),
        open_vocab: Boolean(m.open_vocab),
    }));
    return data;
}

async function uploadWeightsFile(
    host: string,
    file: File,
    openVocab: boolean,
    isSecure?: boolean,
    arcId?: string | null
): Promise<void> {
    const lower = file.name.trim().toLowerCase();
    if (!lower.endsWith(".pt")) {
        throw new Error("File must be a .pt PyTorch weights file.");
    }
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("open_vocab", openVocab ? "true" : "false");
    const base = requireServiceUrl(getBboxServiceUrl(host, "model/upload", isSecure, arcId));
    const url = `${base}${base.includes("?") ? "&" : "?"}open_vocab=${openVocab ? "true" : "false"}`;
    const res = await authedFetch(url, {
        method: "POST",
        body: fd,
    });
    if (!res.ok) {
        let detail = res.statusText;
        try {
            const err = (await res.json()) as { error?: string };
            if (err.error) detail = err.error;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
}

async function activateWeightsFile(
    host: string,
    filename: string,
    openVocab: boolean,
    isSecure?: boolean,
    arcId?: string | null
): Promise<void> {
    const res = await authedFetch(requireServiceUrl(getBboxServiceUrl(host, "model/active", isSecure, arcId)), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename, open_vocab: openVocab }),
    });
    if (!res.ok) {
        let detail = res.statusText;
        try {
            const err = (await res.json()) as { error?: string };
            if (err.error) detail = err.error;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
}

async function deleteWeightsFile(
    host: string,
    filename: string,
    openVocab: boolean,
    isSecure?: boolean,
    arcId?: string | null
): Promise<void> {
    const base = requireServiceUrl(getBboxServiceUrl(host, "model/file", isSecure, arcId));
    const qs = new URLSearchParams({
        filename,
        open_vocab: openVocab ? "true" : "false",
    });
    const res = await authedFetch(`${base}${base.includes("?") ? "&" : "?"}${qs.toString()}`, {
        method: "DELETE",
    });
    if (!res.ok) {
        let detail = res.statusText;
        try {
            const err = (await res.json()) as { error?: string };
            if (err.error) detail = err.error;
        } catch {
            /* ignore */
        }
        throw new Error(detail);
    }
}

/** Unchecked: dark fill via Tailwind; checked: tick in index.css (.object-recognition-checkbox). */
const RECOGNITION_CHECKBOX_CLASS =
    "object-recognition-checkbox h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-r3 border border-black80 bg-black70 " +
    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary2/80";

const MIN_CONF_RANGE_CLASS =
    "h-4 w-full min-w-[6rem] flex-1 cursor-pointer bg-transparent [color-scheme:dark] " +
    "appearance-none accent-primary2 outline-none disabled:cursor-not-allowed disabled:opacity-50 " +
    "[&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full " +
    "[&::-webkit-slider-runnable-track]:bg-black70 " +
    "[&::-webkit-slider-thumb]:-mt-0.5 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 " +
    "[&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:appearance-none " +
    "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-0 " +
    "[&::-webkit-slider-thumb]:bg-primary2 " +
    "[&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-black70 " +
    "[&::-moz-range-thumb]:h-3 [&::-moz-range-thumb]:w-3 [&::-moz-range-thumb]:cursor-pointer " +
    "[&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-primary2";

const ObjectRecognitionSettingsPanel: React.FC = () => {
    const { t } = useTranslation(["settings", "common"]);
    const { selectedARC } = useARC();
    const { addToast } = useToast();
    const { setBboxOverlaySource } = useBboxOverlaySource();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const { setObjectRecognitionEnabled, refreshInferenceSource } = useWebRTC();
    const host = selectedARC?.host ?? null;

    const [rows, setRows] = useState<RecognitionClassRow[]>([]);
    const [globalEnabled, setGlobalEnabled] = useState(false);
    const [filter, setFilter] = useState("");
    /** True until the first (non-silent) load for the current ARC finishes. */
    const [initialLoading, setInitialLoading] = useState(Boolean(host));
    const [loading, setLoading] = useState(false);

    const [modelCatalog, setModelCatalog] = useState<InferModelEntry[]>([]);
    const [weightsActive, setWeightsActive] = useState("");
    const [openVocab, setOpenVocab] = useState(false);
    const [yoloInferenceMode, setYoloInferenceMode] = useState<YoloInferenceMode>(
        loadYoloInferenceMode
    );
    const [selectedWeightFile, setSelectedWeightFile] = useState("");
    const [uploadBusy, setUploadBusy] = useState(false);
    const [activateBusy, setActivateBusy] = useState(false);
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [modeSwitchBusy, setModeSwitchBusy] = useState(false);
    const [pickedUploadName, setPickedUploadName] = useState("");
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const [globalToggleBusy, setGlobalToggleBusy] = useState(false);
    const [engines, setEngines] = useState<RecognitionEnginesStatus>(ENGINES_DOWN);
    const [imgszWidth, setImgszWidth] = useState(IMGSZ_DEFAULT);
    const [imgszHeight, setImgszHeight] = useState(IMGSZ_DEFAULT);
    const [imgszWidthDraft, setImgszWidthDraft] = useState(String(IMGSZ_DEFAULT));
    const [imgszHeightDraft, setImgszHeightDraft] = useState(String(IMGSZ_DEFAULT));
    const [imgszBusy, setImgszBusy] = useState(false);
    const [inferenceSource, setInferenceSource] = useState<InferenceSourceId>("front");
    const [sourceAvailability, setSourceAvailability] = useState<InferenceSourceEntry[]>([]);
    const [sourceBusy, setSourceBusy] = useState(false);

    const openVocabModels = useMemo(
        () => modelCatalog.filter((m) => m.open_vocab).map((m) => m.name),
        [modelCatalog]
    );
    const closedVocabModels = useMemo(
        () => modelCatalog.filter((m) => !m.open_vocab).map((m) => m.name),
        [modelCatalog]
    );
    const modelsForMode =
        yoloInferenceMode === "open_vocab" ? openVocabModels : closedVocabModels;
    const showOpenVocabUi = yoloInferenceMode === "open_vocab";
    const uploadAsOpenVocab = yoloInferenceMode === "open_vocab";

    const getYoloPromptsUrl = useCallback(() => {
        if (!host) return null;
        return getBboxServiceUrl(host, "prompts", isSecure, arcId);
    }, [host, isSecure, arcId]);

    const load = useCallback(async (options?: { silent?: boolean }) => {
        if (!host) return;
        const silent = options?.silent === true;
        if (!silent) setLoading(true);
        try {
            const [status, data, catalog, inferSettings, inferSource] = await Promise.all([
                probeRecognitionEngines(host, isSecure, arcId),
                fetchRecognition(host, isSecure, arcId).catch(() => null),
                fetchModelList(host, isSecure, arcId).catch(() => null),
                fetchInferSettings(host, isSecure, arcId).catch(() => null),
                fetchInferSource(host, isSecure, arcId).catch(() => null),
            ]);
            setEngines(status);
            // NanoOWL is retired; overlay always comes from YOLO.
            setBboxOverlaySource("yolo26");

            const recognitionEnabled = Boolean(status.yolo26.enabled);
            setRows(data?.classes ?? []);
            setGlobalEnabled(recognitionEnabled);
            setObjectRecognitionEnabled(recognitionEnabled);
            if (inferSettings) {
                setImgszWidth(inferSettings.imgsz_width);
                setImgszHeight(inferSettings.imgsz_height);
                setImgszWidthDraft(String(inferSettings.imgsz_width));
                setImgszHeightDraft(String(inferSettings.imgsz_height));
            }
            if (inferSource) {
                setInferenceSource(inferSource.source);
                setSourceAvailability(inferSource.available);
                refreshInferenceSource(inferSource.source);
            }

            if (catalog) {
                const entries = catalog.models;
                setModelCatalog(entries);
                setWeightsActive(catalog.active);
                const ov =
                    typeof catalog.open_vocab === "boolean"
                        ? catalog.open_vocab
                        : false;
                setOpenVocab(ov);
                setSelectedWeightFile((prev) => {
                    const mode = loadYoloInferenceMode();
                    const pool = entries
                        .filter((m) =>
                            mode === "open_vocab" ? m.open_vocab : !m.open_vocab
                        )
                        .map((m) => m.name);
                    if (prev && pool.indexOf(prev) >= 0) return prev;
                    if (catalog.active && pool.indexOf(catalog.active) >= 0) {
                        return catalog.active;
                    }
                    return pool[0] ?? "";
                });
            } else if (!status.yolo26.up) {
                setModelCatalog([]);
                setWeightsActive("");
                setSelectedWeightFile("");
                setOpenVocab(false);
            }

            if (!status.yolo26.up && !silent) {
                addToast({
                    type: "error",
                    message: t("objectRecognition.loadFailed"),
                });
            }
        } catch (e) {
            setEngines(ENGINES_DOWN);
            setRows([]);
            setGlobalEnabled(false);
            setObjectRecognitionEnabled(false);
            addToast({
                type: "error",
                message:
                    e instanceof Error
                        ? toUserFacingApiError(e, t("objectRecognition.requestFailed"))
                        : t("objectRecognition.loadFailed"),
            });
        } finally {
            if (!silent) setLoading(false);
            setInitialLoading(false);
        }
    }, [host, isSecure, arcId, addToast, setBboxOverlaySource, setObjectRecognitionEnabled, refreshInferenceSource, t]);

    useEffect(() => {
        setInitialLoading(Boolean(host));
        setGlobalEnabled(false);
        setRows([]);
        setEngines(ENGINES_DOWN);
        if (host) {
            void load();
        } else {
            setInitialLoading(false);
        }
        // Re-run when ARC host changes; load identity tracks host/isSecure/arcId.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional host-gated reset
    }, [host, isSecure, arcId]);

    const filtered = useMemo(() => {
        const q = filter.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => r.name.toLowerCase().includes(q));
    }, [rows, filter]);

    const allEnabled =
        rows.length > 0 && rows.every((r) => r.enabled);
    const someEnabled = rows.some((r) => r.enabled);
    const selectAllRef = useRef<HTMLInputElement>(null);
    const confSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (confSaveTimerRef.current) {
                clearTimeout(confSaveTimerRef.current);
            }
        };
    }, []);

    const persistRecognitionRows = useCallback(
        async (classRows: RecognitionClassRow[]) => {
            if (!host || classRows.length === 0 || openVocab) return;
            try {
                await putRecognition(
                    host,
                    { enabled: true, classes: classRows },
                    isSecure,
                    arcId,
                );
            } catch (e) {
                addToast({
                    type: "error",
                    message:
                        e instanceof Error
                            ? toUserFacingApiError(e, t("objectRecognition.requestFailed"))
                            : t("objectRecognition.saveFailed"),
                });
            }
        },
        [host, isSecure, arcId, openVocab, addToast, t],
    );

    const schedulePersistRows = useCallback(
        (next: RecognitionClassRow[]) => {
            if (confSaveTimerRef.current) {
                clearTimeout(confSaveTimerRef.current);
            }
            confSaveTimerRef.current = setTimeout(() => {
                void persistRecognitionRows(next);
            }, 400);
        },
        [persistRecognitionRows],
    );

    useEffect(() => {
        const el = selectAllRef.current;
        if (el) {
            el.indeterminate =
                rows.length > 0 && someEnabled && !allEnabled;
        }
    }, [rows, someEnabled, allEnabled]);

    const toggleSelectAll = useCallback(() => {
        const enable = !allEnabled;
        setRows((prev) => {
            const next = prev.map((r) => ({ ...r, enabled: enable }));
            void persistRecognitionRows(next);
            return next;
        });
    }, [allEnabled, persistRecognitionRows]);

    const updateRow = useCallback(
        (
            id: number,
            patch: Partial<RecognitionClassRow>,
            options?: { debounce?: boolean },
        ) => {
            setRows((prev) => {
                const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));
                if (options?.debounce) {
                    schedulePersistRows(next);
                } else {
                    void persistRecognitionRows(next);
                }
                return next;
            });
        },
        [persistRecognitionRows, schedulePersistRows],
    );

    const toggleGlobalRecognition = useCallback(async () => {
        if (!host) return;
        const next = !globalEnabled;
        setGlobalToggleBusy(true);
        try {
            // video_pipeline always exports SHM when configured; gate inference via HTTP only.
            // Soft-disable peers (unreachable = already off); hard-enable YOLO when turning on.
            if (!next) {
                await disableAllRecognitionEngines(host, isSecure, arcId);
                setEngines((prev) => ({
                    yolo26: { up: prev.yolo26.up, enabled: false },
                }));
            } else {
                if (!engines.yolo26.up) {
                    throw new Error(t("objectRecognition.noEngineReachable"));
                }
                setBboxOverlaySource("yolo26");
                await applyBboxOverlaySource(host, "yolo26", isSecure, arcId);
                setEngines((prev) => ({
                    yolo26: { up: true, enabled: true },
                }));
            }
            setGlobalEnabled(next);
            setObjectRecognitionEnabled(next);
            addToast({
                type: "success",
                message: next
                    ? t("objectRecognition.recognitionOn")
                    : t("objectRecognition.recognitionOffToast"),
            });
        } catch (e) {
            addToast({
                type: "error",
                message:
                    e instanceof Error
                        ? toUserFacingApiError(e, t("objectRecognition.requestFailed"))
                        : t("objectRecognition.updateFailed"),
            });
        } finally {
            setGlobalToggleBusy(false);
        }
    }, [
        host,
        isSecure,
        arcId,
        globalEnabled,
        engines,
        addToast,
        setBboxOverlaySource,
        setObjectRecognitionEnabled,
        t,
    ]);

    const applyImgsz = useCallback(async () => {
        if (!host) return;
        const parseDim = (raw: string): number | null => {
            const n = Number(raw.trim());
            if (!Number.isFinite(n)) return null;
            return Math.max(IMGSZ_MIN, Math.min(IMGSZ_MAX, Math.round(n)));
        };
        const nextW = parseDim(imgszWidthDraft);
        const nextH = parseDim(imgszHeightDraft);
        if (nextW == null || nextH == null) {
            addToast({
                type: "error",
                message: t("objectRecognition.imgszInvalid"),
            });
            return;
        }
        if (nextW === imgszWidth && nextH === imgszHeight) {
            setImgszWidthDraft(String(nextW));
            setImgszHeightDraft(String(nextH));
            return;
        }
        setImgszBusy(true);
        try {
            const saved = await putInferImgsz(
                host,
                { width: nextW, height: nextH },
                isSecure,
                arcId,
            );
            setImgszWidth(saved.width);
            setImgszHeight(saved.height);
            setImgszWidthDraft(String(saved.width));
            setImgszHeightDraft(String(saved.height));
            addToast({
                type: "success",
                message: t("objectRecognition.imgszSaved", {
                    width: saved.width,
                    height: saved.height,
                }),
            });
        } catch (e) {
            addToast({
                type: "error",
                message:
                    e instanceof Error
                        ? toUserFacingApiError(e, t("objectRecognition.requestFailed"))
                        : t("objectRecognition.imgszSaveFailed"),
            });
        } finally {
            setImgszBusy(false);
        }
    }, [
        host,
        isSecure,
        arcId,
        imgszWidth,
        imgszHeight,
        imgszWidthDraft,
        imgszHeightDraft,
        addToast,
        t,
    ]);

    const onPickUpload = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const f = e.target.files?.[0];
            if (!f) {
                setPickedUploadName("");
                return;
            }
            if (!f.name.toLowerCase().endsWith(".pt")) {
                addToast({
                    type: "error",
                    message: t("objectRecognition.choosePtFile"),
                });
                e.target.value = "";
                setPickedUploadName("");
                return;
            }
            setPickedUploadName(f.name);
        },
        [addToast, t]
    );

    const doUpload = useCallback(async () => {
        if (!host) return;
        const input = uploadInputRef.current;
        const file = input?.files?.[0];
        if (!file) {
            addToast({ type: "error", message: t("objectRecognition.choosePtFirst") });
            return;
        }
        if (!file.name.toLowerCase().endsWith(".pt")) {
            addToast({ type: "error", message: t("objectRecognition.fileMustBePt") });
            return;
        }
        setUploadBusy(true);
        try {
            await uploadWeightsFile(host, file, uploadAsOpenVocab, isSecure, arcId);
            addToast({
                type: "success",
                message: t("objectRecognition.uploaded", { name: file.name }),
            });
            if (input) input.value = "";
            setPickedUploadName("");
            await load();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("objectRecognition.uploadFailed")),
            });
        } finally {
            setUploadBusy(false);
        }
    }, [host, isSecure, arcId, load, addToast, t, uploadAsOpenVocab]);

    const doActivateModel = useCallback(async () => {
        if (!host || !selectedWeightFile) return;
        const asOpen = yoloInferenceMode === "open_vocab";
        setActivateBusy(true);
        try {
            await activateWeightsFile(host, selectedWeightFile, asOpen, isSecure, arcId);
            setYoloInferenceMode(asOpen ? "open_vocab" : "uploaded_model");
            storeYoloInferenceMode(asOpen ? "open_vocab" : "uploaded_model");
            addToast({
                type: "success",
                message: t("objectRecognition.nowUsing", { name: selectedWeightFile }),
            });
            await load();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("objectRecognition.switchModelFailed")),
            });
        } finally {
            setActivateBusy(false);
        }
    }, [host, isSecure, arcId, selectedWeightFile, yoloInferenceMode, load, addToast, t]);

    const doDeleteModel = useCallback(async () => {
        if (!host || !selectedWeightFile) return;
        const asOpen = yoloInferenceMode === "open_vocab";
        if (
            weightsActive === selectedWeightFile &&
            openVocab === asOpen
        ) {
            addToast({
                type: "error",
                message: t("objectRecognition.cannotDeleteActive"),
            });
            return;
        }
        setDeleteBusy(true);
        try {
            await deleteWeightsFile(host, selectedWeightFile, asOpen, isSecure, arcId);
            addToast({
                type: "success",
                message: t("objectRecognition.deleted", { name: selectedWeightFile }),
            });
            setSelectedWeightFile("");
            await load();
        } catch (e) {
            addToast({
                type: "error",
                message: toUserFacingApiError(e, t("objectRecognition.deleteFailed")),
            });
        } finally {
            setDeleteBusy(false);
        }
    }, [
        host,
        selectedWeightFile,
        yoloInferenceMode,
        weightsActive,
        openVocab,
        isSecure,
        arcId,
        load,
        addToast,
        t,
    ]);

    const selectYoloInferenceMode = useCallback(
        async (mode: YoloInferenceMode) => {
            if (mode === yoloInferenceMode && !modeSwitchBusy) {
                // Still ensure device weights match preference if out of sync.
                const matches =
                    mode === "open_vocab" ? openVocab : !openVocab;
                if (matches) return;
            }
            if (!host) return;

            const pool =
                mode === "open_vocab" ? openVocabModels : closedVocabModels;
            if (pool.length === 0) {
                setYoloInferenceMode(mode);
                storeYoloInferenceMode(mode);
                setSelectedWeightFile("");
                addToast({
                    type: "error",
                    message:
                        mode === "open_vocab"
                            ? t("objectRecognition.noOpenVocabModel")
                            : t("objectRecognition.noUploadedModel"),
                });
                return;
            }

            const needActivate =
                (mode === "open_vocab" && !openVocab) ||
                (mode === "uploaded_model" && openVocab);
            const target =
                (weightsActive && pool.indexOf(weightsActive) >= 0
                    ? weightsActive
                    : null) ||
                (selectedWeightFile && pool.indexOf(selectedWeightFile) >= 0
                    ? selectedWeightFile
                    : null) ||
                pool[0]!;

            setYoloInferenceMode(mode);
            storeYoloInferenceMode(mode);
            setSelectedWeightFile(target);

            if (!needActivate && weightsActive === target) {
                return;
            }

            setModeSwitchBusy(true);
            try {
                await activateWeightsFile(
                    host,
                    target,
                    mode === "open_vocab",
                    isSecure,
                    arcId
                );
                addToast({
                    type: "success",
                    message:
                        mode === "open_vocab"
                            ? t("objectRecognition.usingOpenVocab")
                            : t("objectRecognition.usingUploadedModel", {
                                  name: target,
                              }),
                });
                await load({ silent: true });
            } catch (e) {
                addToast({
                    type: "error",
                    message:
                        e instanceof Error
                            ? toUserFacingApiError(e, t("objectRecognition.requestFailed"))
                            : t("objectRecognition.switchModelFailed"),
                });
            } finally {
                setModeSwitchBusy(false);
            }
        },
        [
            yoloInferenceMode,
            modeSwitchBusy,
            openVocab,
            host,
            openVocabModels,
            closedVocabModels,
            weightsActive,
            selectedWeightFile,
            isSecure,
            arcId,
            addToast,
            load,
            t,
        ]
    );

    const gimbalSourceReady =
        sourceAvailability.find((entry) => entry.id === "gimbal")?.ready === true;

    const selectInferenceSource = useCallback(
        async (next: InferenceSourceId) => {
            if (!host || next === inferenceSource) return;
            if (next === "gimbal" && !gimbalSourceReady) {
                addToast({
                    type: "error",
                    message: t("objectRecognition.sourceGimbalUnavailable"),
                });
                return;
            }
            setSourceBusy(true);
            try {
                const updated = await putInferSource(host, next, isSecure, arcId);
                setInferenceSource(updated.source);
                setSourceAvailability(updated.available);
                refreshInferenceSource(updated.source);
                addToast({
                    type: "success",
                    message: t("objectRecognition.sourceSwitched", {
                        source:
                            updated.source === "gimbal"
                                ? t("objectRecognition.sourceGimbal")
                                : t("objectRecognition.sourceFront"),
                    }),
                });
            } catch (e) {
                addToast({
                    type: "error",
                    message:
                        e instanceof Error
                            ? toUserFacingApiError(e, t("objectRecognition.requestFailed"))
                            : t("objectRecognition.sourceSwitchFailed"),
                });
            } finally {
                setSourceBusy(false);
            }
        },
        [
            host,
            inferenceSource,
            gimbalSourceReady,
            isSecure,
            arcId,
            addToast,
            refreshInferenceSource,
            t,
        ],
    );

    if (!host) {
        return (
            <div className="max-w-3xl">
                <p className="text-h4 font-semibold text-white">{t("objectRecognition.title")}</p>
                <p className="mt-3 text-h5 text-black40">
                    {t("objectRecognition.selectArc")}
                </p>
            </div>
        );
    }

    if (initialLoading) {
        return (
            <div className="flex max-w-4xl flex-col gap-4">
                <p className="text-h4 font-semibold text-white">{t("objectRecognition.title")}</p>
                <div
                    className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-r10 border border-black80 bg-black90 px-4 py-10"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                >
                    <div
                        className="h-8 w-8 rounded-full border-2 border-primary2 border-t-transparent animate-spin"
                        aria-hidden
                    />
                    <p className="text-h5 text-black40">{t("objectRecognition.loading")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex max-w-4xl flex-col gap-4">
            <div>
                <p className="text-h4 font-semibold text-white">{t("objectRecognition.title")}</p>
            </div>

            <div className="flex items-center justify-between gap-3 rounded-r10 border border-black80 bg-black90 px-3 py-2">
                <p className="shrink-0 text-h5 font-semibold text-white">{t("objectRecognition.toggleLabel")}</p>
                <div className="flex items-center gap-2">
                    <span className="text-h5 tabular-nums text-black40">
                        {globalEnabled ? t("common:on") : t("common:off")}
                    </span>
                    <button
                        type="button"
                        role="switch"
                        aria-checked={globalEnabled}
                        aria-label={t("objectRecognition.toggleAriaLabel")}
                        disabled={loading || globalToggleBusy}
                        onClick={() => void toggleGlobalRecognition()}
                        className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border p-0.5 transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary2/90 focus-visible:ring-offset-2 focus-visible:ring-offset-black90 disabled:cursor-not-allowed disabled:opacity-50 ${
                            globalEnabled
                                ? "justify-end border-primary2 bg-primary2"
                                : "justify-start border-black80 bg-black70"
                        }`}
                    >
                        <span
                            className="pointer-events-none h-5 w-5 rounded-full bg-white shadow"
                            aria-hidden
                        />
                    </button>
                </div>
            </div>

            <div className="rounded-r10 border border-black80 bg-black90 px-3 py-3">
                <p className="text-h5 font-semibold text-white">
                    {t("objectRecognition.inferenceSource")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                    {t("objectRecognition.inferenceSourceDescription")}
                </p>
                <div
                    className="mt-2 flex w-full max-w-md gap-1 rounded-r5 border border-black80 bg-black70 p-1"
                    role="tablist"
                    aria-label={t("objectRecognition.inferenceSourceAria")}
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={inferenceSource === "front"}
                        disabled={loading || sourceBusy || !engines.yolo26.up}
                        onClick={() => void selectInferenceSource("front")}
                        className={`flex-1 rounded-r3 px-3 py-2 text-h5 ${
                            inferenceSource === "front"
                                ? "bg-black80 font-semibold text-white"
                                : "text-black40 hover:bg-black80/60 hover:text-white"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        {t("objectRecognition.sourceFront")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={inferenceSource === "gimbal"}
                        disabled={
                            loading ||
                            sourceBusy ||
                            !engines.yolo26.up ||
                            !gimbalSourceReady
                        }
                        title={
                            gimbalSourceReady
                                ? undefined
                                : t("objectRecognition.sourceGimbalUnavailable")
                        }
                        onClick={() => void selectInferenceSource("gimbal")}
                        className={`flex-1 rounded-r3 px-3 py-2 text-h5 ${
                            inferenceSource === "gimbal"
                                ? "bg-black80 font-semibold text-white"
                                : "text-black40 hover:bg-black80/60 hover:text-white"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        {t("objectRecognition.sourceGimbal")}
                    </button>
                </div>
            </div>

            <div className="rounded-r10 border border-black80 bg-black90 px-3 py-3">
                <p className="text-h5 font-semibold text-white">
                    {t("objectRecognition.imgszLabel")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                    {t("objectRecognition.imgszDescription")}
                </p>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                    <label className="flex min-w-[6rem] flex-col gap-1">
                        <span className="text-h5 text-black40">
                            {t("objectRecognition.imgszWidth")}
                        </span>
                        <input
                            type="number"
                            inputMode="numeric"
                            min={IMGSZ_MIN}
                            max={IMGSZ_MAX}
                            step={1}
                            value={imgszWidthDraft}
                            disabled={loading || imgszBusy || !engines.yolo26.up}
                            onChange={(e) => setImgszWidthDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") void applyImgsz();
                            }}
                            className="w-24 rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none disabled:opacity-50"
                            aria-label={t("objectRecognition.imgszWidthAria")}
                        />
                    </label>
                    <label className="flex min-w-[6rem] flex-col gap-1">
                        <span className="text-h5 text-black40">
                            {t("objectRecognition.imgszHeight")}
                        </span>
                        <input
                            type="number"
                            inputMode="numeric"
                            min={IMGSZ_MIN}
                            max={IMGSZ_MAX}
                            step={1}
                            value={imgszHeightDraft}
                            disabled={loading || imgszBusy || !engines.yolo26.up}
                            onChange={(e) => setImgszHeightDraft(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") void applyImgsz();
                            }}
                            className="w-24 rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none disabled:opacity-50"
                            aria-label={t("objectRecognition.imgszHeightAria")}
                        />
                    </label>
                    <button
                        type="button"
                        disabled={loading || imgszBusy || !engines.yolo26.up}
                        onClick={() => void applyImgsz()}
                        className="shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {t("objectRecognition.imgszApply")}
                    </button>
                    <span className="pb-2 text-h5 text-black40">
                        {t("objectRecognition.imgszHint")}
                    </span>
                </div>
            </div>

            {!engines.yolo26.up && !loading && (
                <div className="rounded-r10 border border-black80 bg-black90 px-3 py-2 text-h5 text-black40">
                    {t("objectRecognition.bothEnginesDown")}
                </div>
            )}

            {globalEnabled ? (
                <>
            <div className="rounded-r10 border border-black80 bg-black90 px-3 py-3">
                <p className="text-h5 font-semibold text-white">
                    {t("objectRecognition.detectionEngine")}
                </p>
                <p className="mt-1 text-h5 text-black40">
                    {t("objectRecognition.detectionEngineDescription")}
                </p>
                <div
                    className="mt-2 flex w-full max-w-md gap-1 rounded-r5 border border-black80 bg-black70 p-1"
                    role="tablist"
                    aria-label={t("objectRecognition.detectionSourceAriaLabel")}
                >
                    <button
                        type="button"
                        role="tab"
                        aria-selected={yoloInferenceMode === "open_vocab"}
                        disabled={loading || modeSwitchBusy || activateBusy}
                        onClick={() => void selectYoloInferenceMode("open_vocab")}
                        className={`flex-1 rounded-r3 px-3 py-2 text-h5 ${
                            yoloInferenceMode === "open_vocab"
                                ? "bg-black80 font-semibold text-white"
                                : "text-black40 hover:bg-black80/60 hover:text-white"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        {t("objectRecognition.modeOpenVocab")}
                    </button>
                    <button
                        type="button"
                        role="tab"
                        aria-selected={yoloInferenceMode === "uploaded_model"}
                        disabled={loading || modeSwitchBusy || activateBusy}
                        onClick={() => void selectYoloInferenceMode("uploaded_model")}
                        className={`flex-1 rounded-r3 px-3 py-2 text-h5 ${
                            yoloInferenceMode === "uploaded_model"
                                ? "bg-black80 font-semibold text-white"
                                : "text-black40 hover:bg-black80/60 hover:text-white"
                        } disabled:cursor-not-allowed disabled:opacity-50`}
                    >
                        {t("objectRecognition.modeUploadedModel")}
                    </button>
                </div>
            </div>

            {showOpenVocabUi ? (
                <>
                    {openVocabModels.length === 0 ? (
                        <div className="rounded-r10 border border-black80 bg-black90 px-3 py-3 text-h5 text-black40">
                            {t("objectRecognition.noOpenVocabModel")}
                        </div>
                    ) : null}
                    <div className="grid gap-4 rounded-r10 border border-black80 bg-black90 p-3 sm:grid-cols-2">
                        <div className="flex flex-col gap-2">
                            <p className="text-h5 font-semibold text-white">
                                {t("objectRecognition.uploadModel")}
                            </p>
                            <p className="text-h5 text-black40">
                                {t("objectRecognition.activeModel")}{" "}
                                <span className="text-white">{weightsActive || "—"}</span>
                                {openVocab ? (
                                    <span className="ml-2 text-black40">(YOLOE)</span>
                                ) : null}
                            </p>
                            <input
                                ref={uploadInputRef}
                                type="file"
                                accept=".pt,application/octet-stream"
                                className="hidden"
                                onChange={onPickUpload}
                                aria-label={t("objectRecognition.chooseWeightsAriaLabel")}
                            />
                            <div className="flex flex-wrap items-center gap-2">
                                <button
                                    type="button"
                                    disabled={loading || uploadBusy}
                                    onClick={() => uploadInputRef.current?.click()}
                                    className="shrink-0 rounded-r5 border border-black80 bg-black80 px-3 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t("objectRecognition.chooseFile")}
                                </button>
                                <span className="min-w-[8rem] flex-1 truncate text-h5 text-black40">
                                    {pickedUploadName || t("objectRecognition.noFileChosen")}
                                </span>
                                <button
                                    type="button"
                                    disabled={loading || uploadBusy || !pickedUploadName}
                                    onClick={() => void doUpload()}
                                    className="shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t("common:upload")}
                                </button>
                            </div>
                        </div>
                        <div className="flex flex-col gap-2">
                            <p className="text-h5 font-semibold text-white">
                                {t("objectRecognition.selectModel")}
                            </p>
                            <div className="flex items-center gap-2">
                                <select
                                    value={
                                        modelsForMode.indexOf(selectedWeightFile) >= 0
                                            ? selectedWeightFile
                                            : modelsForMode[0] ?? ""
                                    }
                                    onChange={(e) => setSelectedWeightFile(e.target.value)}
                                    disabled={
                                        loading ||
                                        activateBusy ||
                                        modeSwitchBusy ||
                                        modelsForMode.length === 0
                                    }
                                    className="min-w-0 flex-1 rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none disabled:opacity-50"
                                    aria-label={t("objectRecognition.weightsSelectAriaLabel")}
                                >
                                    {modelsForMode.length === 0 ? (
                                        <option value="">
                                            {t("objectRecognition.noPtFiles")}
                                        </option>
                                    ) : (
                                        modelsForMode.map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))
                                    )}
                                </select>
                                <button
                                    type="button"
                                    disabled={
                                        loading ||
                                        activateBusy ||
                                        deleteBusy ||
                                        modeSwitchBusy ||
                                        !selectedWeightFile ||
                                        modelsForMode.length === 0
                                    }
                                    onClick={() => void doActivateModel()}
                                    className="box-border shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t("objectRecognition.useModel")}
                                </button>
                                <button
                                    type="button"
                                    disabled={
                                        loading ||
                                        activateBusy ||
                                        deleteBusy ||
                                        modeSwitchBusy ||
                                        !selectedWeightFile ||
                                        modelsForMode.length === 0 ||
                                        (weightsActive === selectedWeightFile && openVocab)
                                    }
                                    onClick={() => void doDeleteModel()}
                                    className="box-border shrink-0 rounded-r5 border border-black80 bg-black80 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t("objectRecognition.removeModel")}
                                </button>
                            </div>
                        </div>
                    </div>
                    {!openVocab && openVocabModels.length > 0 ? (
                        <p className="text-h5 text-black40">
                            {t("objectRecognition.openVocabActivateHint")}
                        </p>
                    ) : null}
                    {openVocab ? (
                        <OpenVocabPromptsPanel
                            getPromptsUrl={getYoloPromptsUrl}
                            reloadKey={weightsActive}
                        />
                    ) : null}
                </>
            ) : (
                <>
            <div className="grid gap-4 rounded-r10 border border-black80 bg-black90 p-3 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                    <p className="text-h5 font-semibold text-white">{t("objectRecognition.uploadModel")}</p>
                    <p className="text-h5 text-black40">
                        {t("objectRecognition.activeModel")}{" "}
                        <span className="text-white">
                            {weightsActive || "—"}
                        </span>
                    </p>
                    <input
                        ref={uploadInputRef}
                        type="file"
                        accept=".pt,application/octet-stream"
                        className="hidden"
                        onChange={onPickUpload}
                        aria-label={t("objectRecognition.chooseWeightsAriaLabel")}
                    />
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            disabled={loading || uploadBusy}
                            onClick={() => uploadInputRef.current?.click()}
                            className="shrink-0 rounded-r5 border border-black80 bg-black80 px-3 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t("objectRecognition.chooseFile")}
                        </button>
                        <span className="min-w-[8rem] flex-1 truncate text-h5 text-black40">
                            {pickedUploadName || t("objectRecognition.noFileChosen")}
                        </span>
                        <button
                            type="button"
                            disabled={loading || uploadBusy || !pickedUploadName}
                            onClick={() => void doUpload()}
                            className="shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t("common:upload")}
                        </button>
                    </div>
                </div>
                <div className="flex flex-col gap-2">
                    <p className="text-h5 font-semibold text-white">{t("objectRecognition.selectModel")}</p>
                    <div className="flex items-center gap-2">
                        <select
                            value={
                                modelsForMode.indexOf(selectedWeightFile) >= 0
                                    ? selectedWeightFile
                                    : modelsForMode[0] ?? ""
                            }
                            onChange={(e) => setSelectedWeightFile(e.target.value)}
                            disabled={
                                loading ||
                                activateBusy ||
                                modeSwitchBusy ||
                                modelsForMode.length === 0
                            }
                            className="min-w-0 flex-1 rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white focus:border-primary2 focus:outline-none disabled:opacity-50"
                            aria-label={t("objectRecognition.weightsSelectAriaLabel")}
                        >
                            {modelsForMode.length === 0 ? (
                                <option value="">{t("objectRecognition.noPtFiles")}</option>
                            ) : (
                                modelsForMode.map((n) => (
                                    <option key={n} value={n}>
                                        {n}
                                    </option>
                                ))
                            )}
                        </select>
                        <button
                            type="button"
                            disabled={
                                loading ||
                                activateBusy ||
                                deleteBusy ||
                                modeSwitchBusy ||
                                !selectedWeightFile ||
                                modelsForMode.length === 0
                            }
                            onClick={() => void doActivateModel()}
                            className="box-border shrink-0 rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-primary1 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t("objectRecognition.useModel")}
                        </button>
                        <button
                            type="button"
                            disabled={
                                loading ||
                                activateBusy ||
                                deleteBusy ||
                                modeSwitchBusy ||
                                !selectedWeightFile ||
                                modelsForMode.length === 0 ||
                                (weightsActive === selectedWeightFile && !openVocab)
                            }
                            onClick={() => void doDeleteModel()}
                            className="box-border shrink-0 rounded-r5 border border-black80 bg-black80 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {t("objectRecognition.removeModel")}
                        </button>
                    </div>
                </div>
            </div>

            {closedVocabModels.length === 0 ? (
                <div className="rounded-r10 border border-black80 bg-black90 px-3 py-3 text-h5 text-black40">
                    {t("objectRecognition.noUploadedModel")}
                </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
                <input
                    type="search"
                    placeholder={t("objectRecognition.filterPlaceholder")}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="min-w-[12rem] flex-1 rounded-r5 border border-black80 bg-black70 px-3 py-2 text-h5 text-white placeholder:text-black60 focus:border-primary2 focus:outline-none"
                />
                <button
                    type="button"
                    disabled={loading}
                    onClick={() => void load()}
                    className="box-border flex w-24 shrink-0 items-center justify-center whitespace-nowrap rounded-r5 border border-black80 bg-black80 px-4 py-2 !normal-case text-h5 leading-none text-white hover:bg-black70 disabled:cursor-not-allowed disabled:text-black40 disabled:hover:bg-black80"
                >
                    {t("common:refresh")}
                </button>
            </div>

            <div className="max-h-[min(32rem,calc(100vh-12rem))] overflow-y-auto rounded-r10 border border-black80 bg-black90">
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-black80 bg-black90 px-3 py-2">
                    <input
                        ref={selectAllRef}
                        id="object-recognition-select-all"
                        type="checkbox"
                        checked={allEnabled}
                        onChange={toggleSelectAll}
                        disabled={loading || rows.length === 0 || openVocab}
                        className={`${RECOGNITION_CHECKBOX_CLASS} disabled:opacity-50 disabled:cursor-not-allowed`}
                        aria-label={t("objectRecognition.selectAllAriaLabel")}
                    />
                    <label
                        htmlFor="object-recognition-select-all"
                        className="cursor-pointer select-none text-h5 text-white"
                    >
                        {t("objectRecognition.selectAll")}
                    </label>
                </div>
                <table className="w-full border-collapse text-left text-h5">
                    <thead className="bg-black80 text-black40">
                        <tr>
                            <th className="w-10 p-2 font-medium">{t("objectRecognition.columnOn")}</th>
                            <th className="p-2 font-medium">{t("objectRecognition.columnClass")}</th>
                            <th className="w-24 p-2 font-medium">{t("objectRecognition.columnId")}</th>
                            <th className="min-w-[12rem] p-2 font-medium">
                                {t("objectRecognition.columnMinConfidence")}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((r) => (
                            <tr
                                key={r.id}
                                className="border-t border-black80 hover:bg-black80/60"
                            >
                                <td className="p-2">
                                    <input
                                        type="checkbox"
                                        checked={r.enabled}
                                        onChange={(e) =>
                                            updateRow(r.id, { enabled: e.target.checked })
                                        }
                                        className={RECOGNITION_CHECKBOX_CLASS}
                                        aria-label={t("objectRecognition.enableClassAriaLabel", { name: r.name })}
                                    />
                                </td>
                                <td className="p-2 text-white">{r.name}</td>
                                <td className="p-2 text-black40">{r.id}</td>
                                <td className="p-2">
                                    <div className="flex min-w-[11rem] max-w-[16rem] items-center gap-2">
                                        <input
                                            type="range"
                                            min={0}
                                            max={1}
                                            step={0.05}
                                            value={r.min_conf}
                                            disabled={!r.enabled}
                                            onChange={(e) => {
                                                const v = parseFloat(e.target.value);
                                                if (Number.isFinite(v)) {
                                                    updateRow(
                                                        r.id,
                                                        {
                                                            min_conf: Math.max(
                                                                0,
                                                                Math.min(1, v),
                                                            ),
                                                        },
                                                        { debounce: true },
                                                    );
                                                }
                                            }}
                                            className={MIN_CONF_RANGE_CLASS}
                                            aria-label={t("objectRecognition.minConfidenceAriaLabel", { name: r.name })}
                                        />
                                        <span className="w-10 shrink-0 tabular-nums text-black40">
                                            {r.min_conf.toFixed(2)}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length === 0 && !loading && (
                    <p className="p-4 text-h5 text-black40">
                        {t("objectRecognition.noClassesLoaded")}
                    </p>
                )}
            </div>

            <PostProcessRulesPanel
                host={host}
                classLabels={rows.map((r) => r.name)}
                disabled={loading || !globalEnabled}
            />
                </>
            )}
                </>
            ) : (
                <div className="rounded-r10 border border-black80 bg-black90 px-3 py-4 text-h5 text-black40">
                    {t("objectRecognition.recognitionOff")}
                </div>
            )}
        </div>
    );
};

export default ObjectRecognitionSettingsPanel;
